import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, Wind, Smile, ArrowUpCircle, ArrowBigUp, Minimize2, Zap, Layers, User, Heart, CircleDot, Sparkles, Eye, Star, ScanFace, Activity, Ear, Dot, Minus, Droplets, PackageOpen, Shirt, Footprints, Target, Orbit, ChevronsDown } from "lucide-react";
import SEO from "@/components/SEO";
import { WHATSAPP_URL as WHATSAPP_LINK, TEL_URL as PHONE_LINK } from "@/constants/contact";

const procedures = [
  {
    name: "Rhinoplasty",
    description: "Nose reshaping for improved appearance and breathing",
    href: "/rhinoplasty-in-mumbai",
    icon: Wind,
  },
  {
    name: "Chin Augmentation",
    description: "Chin implant and genioplasty for profile balance and jawline definition",
    href: "/chin-augmentation-in-mumbai",
    icon: CircleDot,
  },
  {
    name: "Cheek Augmentation",
    description: "Cheek implants, fillers, and fat grafting for mid-face volume and contour",
    href: "/cheek-augmentation-in-mumbai",
    icon: Sparkles,
  },
  {
    name: "Facelift",
    description: "Reduce visible signs of facial ageing naturally",
    href: "/facelift-surgery-mumbai",
    icon: Smile,
  },
  {
    name: "Neck Lift",
    description: "Platysmaplasty and neck contouring for bands, lax skin, and jawline definition",
    href: "/neck-lift-surgery-mumbai",
    icon: Activity,
  },
  {
    name: "Double Chin Removal",
    description: "Submental liposuction, injectables, and tightening — surgical and non-surgical options matched to fat vs. lax skin",
    href: "/double-chin-removal-mumbai",
    icon: ChevronsDown,
  },
  {
    name: "Ear Reshaping (Otoplasty)",
    description: "Otoplasty for protruding or asymmetric ears — children from age 5 and adults, natural results",
    href: "/otoplasty-ear-reshaping-surgery-mumbai",
    icon: Ear,
  },
  {
    name: "Earlobe Repair",
    description: "Lobuloplasty for torn, split, stretched, or gauged earlobes — local anesthesia, minimal downtime, re-piercing after healing",
    href: "/earlobe-repair-surgery-mumbai",
    icon: Ear,
  },
  {
    name: "Blepharoplasty",
    description: "Upper and lower eyelid surgery for a rested, natural look",
    href: "/blepharoplasty-eyelid-surgery-mumbai",
    icon: Eye,
  },
  {
    name: "Dimple Creation",
    description: "Dimpleplasty for natural-looking cheek or chin dimples, no visible scars",
    href: "/dimple-creation-surgery-mumbai",
    icon: Star,
  },
  {
    name: "Buccal Fat Removal",
    description: "Slimmer lower cheeks and sharper contour via buccal lipectomy, no external scars",
    href: "/buccal-fat-removal-mumbai",
    icon: ScanFace,
  },
  {
    name: "Breast Augmentation",
    description: "Breast implant surgery for volume, shape, and proportion — silicone or saline",
    href: "/breast-implant-surgery-mumbai",
    icon: ArrowUpCircle,
  },
  {
    name: "Breast Implant Removal",
    description: "Explant surgery — safe removal with capsule management, lift, or fat grafting options",
    href: "/breast-implant-removal-surgery-mumbai",
    icon: PackageOpen,
  },
  {
    name: "Breast Lift",
    description: "Mastopexy to raise sagging breasts, reposition nipples, and remove excess skin",
    href: "/breast-lift-surgery-mumbai",
    icon: ArrowBigUp,
  },
  {
    name: "Breast Reduction",
    description: "Reduction mammoplasty for macromastia — relieve pain, grooves, rashes, and improve proportion",
    href: "/breast-reduction-surgery-mumbai",
    icon: Minimize2,
  },
  {
    name: "Breast Fat Transfer",
    description: "Natural breast lipofilling using your own fat — modest, implant-free enhancement",
    href: "/breast-fat-transfer-lipofilling-mumbai",
    icon: Droplets,
  },
  {
    name: "Male Nipple Reduction",
    description: "Reduce enlarged or protruding male nipples under local anesthesia — minimal downtime",
    href: "/male-nipple-reduction-mumbai",
    icon: Minus,
  },
  {
    name: "Inverted Nipple Correction",
    description: "Grade 1–3 inverted nipple repair; duct-preserving options when appropriate",
    href: "/inverted-nipple-correction-mumbai",
    icon: Dot,
  },
  {
    name: "Liposuction",
    description: "Remove stubborn fat and contour specific body areas",
    href: "/liposuction-surgery-mumbai",
    icon: Zap,
  },
  {
    name: "Tummy Tuck",
    description: "Remove excess skin and tighten abdominal muscles",
    href: "/tummy-tuck-surgery-mumbai",
    icon: Layers,
  },
  {
    name: "Arm Lift",
    description: "Brachioplasty to remove loose upper arm skin and contour the arms",
    href: "/arm-lift-surgery-mumbai",
    icon: Shirt,
  },
  {
    name: "Thigh Lift",
    description: "Thighplasty to tighten loose inner and outer thigh skin after weight loss or aging",
    href: "/thigh-lift-surgery-mumbai",
    icon: Footprints,
  },
  {
    name: "Buttock Augmentation",
    description: "Brazilian Butt Lift (fat transfer) and buttock implants for shape, projection, and proportion",
    href: "/buttock-augmentation-surgery-mumbai",
    icon: Target,
  },
  {
    name: "Body Lift",
    description: "Lower body lift and belt lipectomy for circumferential skin removal after major weight loss",
    href: "/body-lift-surgery-mumbai",
    icon: Orbit,
  },
  {
    name: "Gynecomastia",
    description: "Flat, masculine chest through male breast reduction",
    href: "/gynecomastia-surgery-mumbai",
    icon: User,
  },
  {
    name: "Mommy Makeover",
    description: "Restore your body after pregnancy in one surgery",
    href: "/mommy-makeover-surgery-mumbai",
    icon: Heart,
  },
];

const cosmeticStructuredData = {
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  name: "Cosmetic Surgery in Mumbai — Dr. Vicky Jain",
  url: "https://drvickyjain.com/cosmetic-surgery-in-mumbai",
  description: "Cosmetic surgery procedures by Dr. Vicky Jain in Mumbai — face, breast, and body procedures with 17+ years of surgical experience.",
  medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
  about: { "@type": "MedicalSpecialty", name: "Cosmetic Surgery" },
  author: { "@type": "Physician", name: "Dr. Vicky Jain", url: "https://drvickyjain.com/about" },
};

const CosmeticSurgeryPage = () => {
  return (
    <div className="bg-warm-white min-h-screen">
      <SEO title="Cosmetic Surgery in Mumbai | Dr. Vicky Jain" description="Cosmetic surgery by Dr. Vicky Jain, Mumbai — face, breast, and body procedures. 17+ years experience. Book a consultation today." canonical="/cosmetic-surgery-in-mumbai" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(cosmeticStructuredData) }} />
      <section className="bg-warm-white pt-32 pb-20 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <p className="text-sm font-medium tracking-widest text-gold uppercase mb-4">Cosmetic Surgery</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-premium-purple mb-6 max-w-3xl">
            Cosmetic Surgery
          </h1>
          <p className="text-lg text-deep-navy/70 max-w-2xl leading-relaxed mb-4">
            Precision surgical procedures designed to enhance your natural features while maintaining authenticity.
          </p>
          <p className="text-base text-deep-navy/60 max-w-2xl leading-relaxed">
            Every procedure is planned around your individual anatomy, proportions, and goals — covering face, nose, eyes, ears, neck, breasts, and body contouring.
          </p>
        </div>
      </section>

      <section className="bg-warm-white py-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {procedures.map((procedure) => (
              <div key={procedure.href} className="bg-white p-8 border border-deep-navy/10">
                <procedure.icon className="w-7 h-7 text-gold mb-5" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
                <p className="text-deep-navy/70 leading-relaxed mb-6">{procedure.description}</p>
                <Link to={procedure.href} className="text-gold font-medium hover:text-deep-navy transition-colors">
                  Learn More →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-premium-purple py-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-serif text-3xl text-warm-white mb-4">Book Your Consultation</h2>
              <p className="text-warm-white/70">
                Every treatment starts with a detailed, one-on-one consultation designed around your goals.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="btn-gold border-none rounded-none px-8 py-6">
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">Book Consultation</a>
              </Button>
              <Button asChild variant="outline" className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6">
                <a href={PHONE_LINK}><Phone className="w-4 h-4 mr-2" />Call Now</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CosmeticSurgeryPage;
